import { toError } from "@clipboard-health/util-ts";
import { RootPaths } from "@src/appV2/App/paths";
import { parseJwtPayload } from "@src/appV2/Auth/cognito/tokenUtils";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
// eslint-disable-next-line no-restricted-imports
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
// eslint-disable-next-line no-restricted-imports
import { getFirebaseSingleton } from "@src/lib/firebase/src";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { exchangeCognitoTokenForFirebase } from "../cognito/tokenExchange";
import { getAllowedImpersonationOrigins } from "./allowedOrigins";
import { useImpersonation } from "./context";
import { postMessageToParent } from "./postMessageToParent";

export type ImpersonateState =
  | "loading"
  | "waiting"
  | "manual-input"
  | "success"
  | "error"
  | "session-ended";

const READY_PING_INTERVAL_MS = 500;
const SECONDS_TO_MS = 1000;

async function clearExistingAuth(): Promise<void> {
  try {
    await getCognitoSingleton().signOut();
  } catch {
    // Best-effort — may not be signed in
  }

  try {
    await getFirebaseSingleton().signOut();
  } catch {
    // Best-effort — may not be signed in
  }
}

interface UseImpersonateAuthResult {
  state: ImpersonateState;
  statusMessage: string;
  errorMessage: string;
  usernameInput: string;
  tokenInput: string;
  usernameFromUrl: string;
  setUsernameInput: (value: string) => void;
  setTokenInput: (value: string) => void;
  authenticate: (username: string, employeeIdToken: string) => Promise<void>;
  startManualAuth: (username: string, token: string) => void;
  setState: (state: ImpersonateState) => void;
}

export function useImpersonateAuth(): UseImpersonateAuthResult {
  const [state, setState] = useState<ImpersonateState>("loading");
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [usernameInput, setUsernameInput] = useState("");
  const [tokenInput, setTokenInput] = useState("");
  const history = useHistory();
  const location = useLocation();
  const { startImpersonation, endImpersonation } = useImpersonation();
  const hasStartedAuth = useRef(false);

  const searchParams = new URLSearchParams(location.search);
  const usernameFromUrl = searchParams.get("username") ?? "";

  const isInIframe = window.parent !== window;

  const authenticate = useCallback(
    async (username: string, employeeIdToken: string) => {
      setState("loading");
      setStatusMessage("Authenticating as impersonator...");

      try {
        const logMetadata = { cognitoUsername: username };

        logEvent(APP_V2_APP_EVENTS.IMPERSONATION_TOKEN_RECEIVED, logMetadata);

        setStatusMessage("Signing in via Cognito...");
        const cognito = getCognitoSingleton();
        const credentials = await cognito.signInAsImpersonator(username, employeeIdToken);

        logEvent(APP_V2_APP_EVENTS.IMPERSONATION_AUTH_SUCCESS, logMetadata);
        setStatusMessage("Exchanging tokens...");

        const firebaseToken = await exchangeCognitoTokenForFirebase();

        const accessTokenPayload = credentials.accessToken
          ? parseJwtPayload(credentials.accessToken)
          : {};
        const impersonatedBy = String(accessTokenPayload.impersonated_by ?? "admin");

        startImpersonation({
          impersonatedBy,
          cognitoUsername: username,
          accessToken: credentials.accessToken ?? "",
        });

        setStatusMessage("Signing in to Firebase...");
        await getFirebaseSingleton().signInWithCustomToken(firebaseToken);

        const tokenExpSeconds = accessTokenPayload.exp ?? 0;
        const expiresAt = tokenExpSeconds * SECONDS_TO_MS;
        setState("success");
        postMessageToParent({ type: "impersonation:success", expiresAt });

        history.replace(RootPaths.APP_V2_HOME);
      } catch (error) {
        endImpersonation();
        await clearExistingAuth();

        logError(APP_V2_APP_EVENTS.IMPERSONATION_AUTH_FAILURE, {
          error,
          metadata: { cognitoUsername: username },
        });

        const message = toError(error).message || "Impersonation authentication failed.";
        setErrorMessage(message);
        setState("error");
        postMessageToParent({ type: "impersonation:error", error: message });
      }
    },
    [history, startImpersonation, endImpersonation]
  );

  const startManualAuth = useCallback(
    (username: string, token: string) => {
      hasStartedAuth.current = true;
      void authenticate(username, token);
    },
    [authenticate]
  );

  // Listen for session end message from parent (admin app closing the session)
  useEffect(() => {
    if (!isInIframe) {
      return undefined;
    }

    const allowedOrigins = getAllowedImpersonationOrigins();

    function handleEndMessage(event: MessageEvent) {
      if (event.source !== window.parent || !allowedOrigins.has(event.origin)) {
        return;
      }

      const data = event.data as { type?: string } | undefined;
      if (data?.type === "impersonation:end") {
        logEvent(APP_V2_APP_EVENTS.IMPERSONATION_CLEANUP_STARTED);
        setState("loading");
        setStatusMessage("Ending session and cleaning up...");
        endImpersonation();
        void clearExistingAuth().then(() => {
          logEvent(APP_V2_APP_EVENTS.IMPERSONATION_CLEANUP_COMPLETE);
          postMessageToParent({ type: "impersonation:cleanupComplete" });
          setState("session-ended");
        });
      }
    }

    window.addEventListener("message", handleEndMessage);
    return () => {
      window.removeEventListener("message", handleEndMessage);
    };
  }, [endImpersonation, isInIframe]);

  useEffect(() => {
    if (hasStartedAuth.current) {
      return;
    }

    logEvent(APP_V2_APP_EVENTS.IMPERSONATION_STARTED, {
      isInIframe,
      hasCognitoUsername: Boolean(usernameFromUrl),
    });

    if (isInIframe) {
      if (!usernameFromUrl) {
        setErrorMessage(
          "Missing username parameter. The admin app must provide a username in the URL."
        );
        setState("error");
        postMessageToParent({
          type: "impersonation:error",
          error: "Missing username query parameter",
        });
        return;
      }

      setState("loading");
      setStatusMessage("Clearing previous session...");

      const allowedOrigins = getAllowedImpersonationOrigins();

      let readyInterval: ReturnType<typeof setInterval> | undefined;
      let isCancelled = false;

      function handleMessage(event: MessageEvent) {
        if (hasStartedAuth.current) {
          return;
        }

        if (event.source !== window.parent || !allowedOrigins.has(event.origin)) {
          return;
        }

        const data = event.data as { type?: string; token?: string } | undefined;
        if (data?.type === "employeeIdToken" && data.token) {
          hasStartedAuth.current = true;
          if (readyInterval) {
            clearInterval(readyInterval);
          }

          void authenticate(usernameFromUrl, data.token);
        }
      }

      window.addEventListener("message", handleMessage);

      // Clear existing auth state before signalling ready
      endImpersonation();
      void clearExistingAuth().then(() => {
        if (isCancelled || hasStartedAuth.current) {
          return;
        }

        setState("waiting");
        setStatusMessage("Waiting for admin app...");
        postMessageToParent({ type: "impersonation:ready" });
        readyInterval = setInterval(() => {
          if (hasStartedAuth.current) {
            clearInterval(readyInterval);
            return;
          }

          postMessageToParent({ type: "impersonation:ready" });
        }, READY_PING_INTERVAL_MS);
      });

      return () => {
        isCancelled = true;
        if (readyInterval) {
          clearInterval(readyInterval);
        }

        window.removeEventListener("message", handleMessage);
      };
    }

    setState("manual-input");
    return undefined;
  }, [isInIframe, usernameFromUrl, authenticate, endImpersonation]);

  return {
    state,
    statusMessage,
    errorMessage,
    usernameInput,
    tokenInput,
    usernameFromUrl,
    setUsernameInput,
    setTokenInput,
    authenticate,
    startManualAuth,
    setState,
  };
}
